<template>
  <div class="statistics-container">
    <!-- 顶部工具栏：标题和筛选合并 -->
    <div class="top-toolbar">
      <div class="toolbar-left">
        <h2 class="page-title-compact">
          <el-icon><DataAnalysis /></el-icon>
          活跃设备统计
        </h2>
      </div>
      <div class="toolbar-right">
        <el-select 
          v-model="selectedGame" 
          placeholder="选择游戏" 
          @change="onGameChange"
          style="width: 200px; margin-right: 12px;"
          size="small"
        >
          <el-option 
            v-for="game in games" 
            :key="game" 
            :label="game" 
            :value="game"
          ></el-option>
        </el-select>
        <el-button 
          type="primary" 
          @click="refreshData"
          :loading="loading"
          :icon="Refresh"
          size="small"
        >
          刷新
        </el-button>
      </div>
    </div>

    <!-- 统计数据展示 -->
    <div class="statistics-content" v-loading="loading">
      <!-- 单个游戏的统计 -->
      <div v-if="selectedGame && singleGameStats.length > 0" class="single-game-stats">
        <!-- 第一行：三个卡片并排 -->
        <el-row :gutter="12" class="main-content-row">
          <!-- 活跃设备数量 -->
          <el-col :span="8">
            <el-card class="chart-card-compact" shadow="never">
              <template #header>
                <div class="card-header-compact">
                  <span class="chart-title">活跃设备数量</span>
                </div>
              </template>
              <div ref="devicesChart" class="chart-container-compact"></div>
            </el-card>
          </el-col>
          <!-- 活跃用户数量 -->
          <el-col :span="8">
            <el-card class="chart-card-compact" shadow="never">
              <template #header>
                <div class="card-header-compact">
                  <span class="chart-title">活跃用户数量</span>
                </div>
              </template>
              <div ref="usersChart" class="chart-container-compact"></div>
            </el-card>
          </el-col>
          <!-- 详细数据表格 -->
          <el-col :span="8">
            <el-card class="table-card-compact" shadow="never">
              <template #header>
                <div class="card-header-compact">
                  <span class="chart-title">详细数据</span>
                </div>
              </template>
              <el-table :data="singleGameStats" stripe size="small" :show-header="true">
                <el-table-column prop="timeRangeLabel" label="时间段" width="80" />
                <el-table-column prop="activeUsers" label="活跃用户" width="90" align="center">
                  <template #default="scope">
                    <el-link 
                      type="primary" 
                      :underline="false" 
                      @click="showActiveUsers(scope.row)"
                      style="cursor: pointer; font-size: 12px;"
                    >
                      <span class="number-highlight">{{ scope.row.activeUsers }}</span>
                    </el-link>
                  </template>
                </el-table-column>
                <el-table-column prop="totalRoles" label="活跃设备" width="90" align="center">
                  <template #default="scope">
                    <span class="number-highlight" style="font-size: 12px;">{{ scope.row.totalRoles }}</span>
                  </template>
                </el-table-column>
                <el-table-column label="平均" width="70" align="center">
                  <template #default="scope">
                    <span v-if="scope.row.activeUsers > 0" style="font-size: 12px;">
                      {{ (scope.row.totalRoles / scope.row.activeUsers).toFixed(1) }}
                    </span>
                    <span v-else>-</span>
                  </template>
                </el-table-column>
              </el-table>
            </el-card>
          </el-col>
        </el-row>
        
        <!-- 第二行：版本设备分布 -->
        <el-row :gutter="12" style="margin-top: 12px;">
          <el-col :span="24">
            <el-card class="chart-card-compact" shadow="never">
              <template #header>
                <div class="card-header-compact">
                  <span class="chart-title">版本设备分布</span>
                </div>
              </template>
              <div v-loading="versionLoading" ref="versionChart" class="chart-container-compact-version"></div>
            </el-card>
          </el-col>
        </el-row>
      </div>

      <!-- 无数据状态 -->
      <div v-else-if="!loading && selectedGame && singleGameStats.length === 0" class="no-data">
        <el-empty description="暂无统计数据">
          <el-button type="primary" @click="refreshData">重新加载</el-button>
        </el-empty>
      </div>
    </div>

    <!-- 活跃用户列表弹窗 -->
    <el-dialog
      v-model="userDialogVisible"
      title="活跃用户列表"
      width="700px"
      :close-on-click-modal="false"
    >
      <div v-loading="userListLoading">
        <div class="user-dialog-header">
          <p><strong>游戏：</strong>{{ currentGame }}</p>
          <p><strong>时间段：</strong>{{ currentTimeRange }}</p>
          <p><strong>用户总数：</strong>{{ userTotal }}</p>
        </div>
        <el-table 
          :data="activeUsersList" 
          stripe 
          max-height="400"
          style="margin-top: 20px;"
        >
          <el-table-column prop="id" label="用户ID" width="100" align="center" />
          <el-table-column prop="username" label="用户名" />
        </el-table>
        <el-pagination
          v-if="userTotal > 0"
          v-model:current-page="userPage"
          v-model:page-size="userPageSize"
          :total="userTotal"
          :page-sizes="[50, 100, 200, 500]"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="handleUserPageSizeChange"
          @current-change="handleUserPageChange"
          style="margin-top: 20px; justify-content: flex-end;"
        />
      </div>
      <template #footer>
        <el-button @click="userDialogVisible = false">关闭</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ref, onMounted, nextTick } from 'vue'
import { ElMessage } from 'element-plus'
import { DataAnalysis, Refresh } from '@element-plus/icons-vue'
import { getUserActivityStatistics, getSupportedGames, getActiveUsersList, getVersionStatistics } from '@/api/statistics'
import * as echarts from 'echarts'

const ADVENTURE_ISLAND_GAMES = ['冒险岛枫星', '冒险岛韩服']

export default {
  name: 'Statistics',
  components: {
    DataAnalysis,
    Refresh
  },
  setup() {
    const loading = ref(false)
    const selectedGame = ref('')
    const games = ref([])
    const singleGameStats = ref([])
    const allGamesStats = ref({})
    
    // 用户列表弹窗相关
    const userDialogVisible = ref(false)
    const userListLoading = ref(false)
    const activeUsersList = ref([])
    const currentGame = ref('')
    const currentTimeRange = ref('')
    const userTotal = ref(0)
    const userPage = ref(1)
    const userPageSize = ref(100)
    const currentIntervalMinutes = ref(null)
    
    // 时间段映射
    const timeRangeMap = {
      '30min': 30,
      '1hour': 60,
      '3hours': 180,
      '6hours': 360,
      '12hours': 720,
      '1day': 1440,
      '2days': 2880
    }
    
    // 图表引用
    const devicesChart = ref(null)
    const usersChart = ref(null)
    const comparisonChart = ref(null)
    const versionChart = ref(null)
    
    // 图表实例
    let devicesChartInstance = null
    let usersChartInstance = null
    let comparisonChartInstance = null
    let versionChartInstance = null
    
    // 版本统计相关
    const versionStatistics = ref([])
    const versionLoading = ref(false)

    // 获取支持的游戏列表
    const loadGames = async () => {
      try {
        console.log('开始获取游戏列表...')
        const response = await getSupportedGames()
        console.log('游戏列表API响应:', response)
        console.log('响应success:', response.success)
        console.log('响应data:', response.data)
        
        // 注意：axios拦截器已经返回了response.data，所以response就是响应体
        if (response.success) {
          games.value = response.data || []
          console.log('设置games.value为:', games.value)
          
          // 设置默认游戏为"冒险岛枫星"
          if (!selectedGame.value && games.value.length > 0) {
            const defaultGame = '冒险岛枫星'
            if (games.value.includes(defaultGame)) {
              selectedGame.value = defaultGame
            } else if (games.value.length > 0) {
              // 如果列表中没有默认游戏，选择第一个
              selectedGame.value = games.value[0]
            }
          }
          
          // 确保游戏列表包含冒险岛游戏（如果后端没有返回，前端兜底）
          for (const name of ADVENTURE_ISLAND_GAMES) {
            if (!games.value.includes(name)) {
              games.value.push(name)
            }
          }
        } else {
          console.error('游戏列表API返回失败:', response.message)
          ElMessage.error('获取游戏列表失败: ' + (response.message || '未知错误'))
        }
      } catch (error) {
        console.error('获取游戏列表失败:', error)
        ElMessage.error('获取游戏列表失败: ' + (error.response?.data?.message || error.message))
      }
    }

    // 初始化图表
    const initCharts = () => {
      nextTick(() => {
        if (selectedGame.value && singleGameStats.value.length > 0) {
          initSingleGameCharts()
          loadVersionStatistics()
        } else if (!selectedGame.value && Object.keys(allGamesStats.value).length > 0) {
          initComparisonChart()
        }
      })
    }
    
    // 加载版本统计
    const loadVersionStatistics = async () => {
      if (!selectedGame.value) {
        return
      }
      
      versionLoading.value = true
      try {
        const response = await getVersionStatistics(selectedGame.value, 60)
        
        if (response.success) {
          versionStatistics.value = response.data || []
          initVersionChart()
        } else {
          ElMessage.error(response.message || '获取版本统计失败')
        }
      } catch (error) {
        console.error('获取版本统计失败:', error)
        ElMessage.error('获取版本统计失败: ' + (error.response?.data?.message || error.message))
      } finally {
        versionLoading.value = false
      }
    }
    
    // 初始化版本统计图表（横向条形图）
    const initVersionChart = () => {
      nextTick(() => {
        if (!versionChart.value) {
          console.warn('versionChart ref 未找到')
          return
        }
        
        if (versionStatistics.value.length === 0) {
          console.warn('版本统计数据为空')
          return
        }
        
        // 销毁现有图表实例
        if (versionChartInstance) {
          versionChartInstance.dispose()
        }
        
        // 创建新的图表实例
        versionChartInstance = echarts.init(versionChart.value)
        
        // 按版本号升序排序（版本号转int后排序）
        const sortedData = [...versionStatistics.value].sort((a, b) => {
          const versionA = parseInt(a.version) || 0
          const versionB = parseInt(b.version) || 0
          return versionA - versionB // 升序
        })
        
        const versions = sortedData.map(item => `v${item.version}`)
        const counts = sortedData.map(item => item.count)
        
        const option = {
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow'
          },
          formatter: function(params) {
            const param = params[0]
            return `${param.name}<br/>设备数量: ${param.value} 个`
          }
        },
        grid: {
          left: '12%',
          right: '8%',
          bottom: '8%',
          top: '8%'
        },
        xAxis: {
          type: 'value',
          name: '设备数量',
          nameTextStyle: {
            fontSize: 11
          },
          axisLabel: {
            fontSize: 11
          }
        },
        yAxis: {
          type: 'category',
          data: versions,
          axisLabel: {
            fontSize: 11
          }
        },
        series: [{
          name: '设备数量',
          type: 'bar',
          data: counts,
          itemStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 1, 0, [
              { offset: 0, color: '#83bff6' },
              { offset: 0.5, color: '#188df0' },
              { offset: 1, color: '#188df0' }
            ])
          },
          emphasis: {
            itemStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 1, 0, [
                { offset: 0, color: '#2378f7' },
                { offset: 0.7, color: '#2378f7' },
                { offset: 1, color: '#83bff6' }
              ])
            }
          },
          label: {
            show: true,
            position: 'right',
            formatter: '{c}',
            fontSize: 11
          }
        }]
        }
        
        versionChartInstance.setOption(option)
      })
    }

    // 初始化单个游戏的图表
    const initSingleGameCharts = () => {
      if (devicesChart.value && usersChart.value) {
        // 销毁现有图表实例
        if (devicesChartInstance) {
          devicesChartInstance.dispose()
        }
        if (usersChartInstance) {
          usersChartInstance.dispose()
        }

        // 创建新的图表实例
        devicesChartInstance = echarts.init(devicesChart.value)
        usersChartInstance = echarts.init(usersChart.value)

        const timeLabels = singleGameStats.value.map(stat => stat.timeRangeLabel)
        const devicesData = singleGameStats.value.map(stat => stat.totalRoles)
        const usersData = singleGameStats.value.map(stat => stat.activeUsers)

        // 活跃设备图表配置
        const devicesOption = {
          tooltip: {
            trigger: 'axis',
            formatter: '{b}<br/>活跃设备: {c} 个'
          },
          xAxis: {
            type: 'category',
            data: timeLabels,
            axisLabel: {
              rotate: 45,
              fontSize: 11
            }
          },
          yAxis: {
            type: 'value',
            name: '设备数量',
            nameTextStyle: {
              fontSize: 11
            },
            axisLabel: {
              fontSize: 11
            }
          },
          series: [{
            data: devicesData,
            type: 'bar',
            itemStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: '#83bff6' },
                { offset: 0.5, color: '#188df0' },
                { offset: 1, color: '#188df0' }
              ])
            },
            emphasis: {
              itemStyle: {
                color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                  { offset: 0, color: '#2378f7' },
                  { offset: 0.7, color: '#2378f7' },
                  { offset: 1, color: '#83bff6' }
                ])
              }
            }
          }],
          grid: {
            left: '8%',
            right: '8%',
            bottom: '12%',
            top: '12%'
          }
        }

        // 活跃用户图表配置
        const usersOption = {
          tooltip: {
            trigger: 'axis',
            formatter: '{b}<br/>活跃用户: {c} 人'
          },
          xAxis: {
            type: 'category',
            data: timeLabels,
            axisLabel: {
              rotate: 45,
              fontSize: 11
            }
          },
          yAxis: {
            type: 'value',
            name: '用户数量',
            nameTextStyle: {
              fontSize: 11
            },
            axisLabel: {
              fontSize: 11
            }
          },
          series: [{
            data: usersData,
            type: 'bar',
            itemStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: '#95d475' },
                { offset: 0.5, color: '#67C23A' },
                { offset: 1, color: '#67C23A' }
              ])
            },
            emphasis: {
              itemStyle: {
                color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                  { offset: 0, color: '#85ce61' },
                  { offset: 0.7, color: '#85ce61' },
                  { offset: 1, color: '#95d475' }
                ])
              }
            }
          }],
          grid: {
            left: '8%',
            right: '8%',
            bottom: '12%',
            top: '12%'
          }
        }

        devicesChartInstance.setOption(devicesOption)
        usersChartInstance.setOption(usersOption)
      }
    }

    // 初始化对比图表
    const initComparisonChart = () => {
      if (comparisonChart.value) {
        // 销毁现有图表实例
        if (comparisonChartInstance) {
          comparisonChartInstance.dispose()
        }

        // 创建新的图表实例
        comparisonChartInstance = echarts.init(comparisonChart.value)

        const gameNames = Object.keys(allGamesStats.value)
        const timeLabels = gameNames.length > 0 ? allGamesStats.value[gameNames[0]].map(stat => stat.timeRangeLabel) : []
        
        const series = gameNames.map((gameName, index) => {
          const gameStats = allGamesStats.value[gameName]
          const colors = ['#5470c6', '#91cc75', '#fac858', '#ee6666', '#73c0de', '#3ba272']
          
          return {
            name: gameName,
            type: 'line',
            smooth: true,
            data: gameStats.map(stat => stat.totalRoles),
            itemStyle: {
              color: colors[index % colors.length]
            },
            lineStyle: {
              width: 3
            }
          }
        })

        const comparisonOption = {
          title: {
            text: '各游戏活跃设备数量对比',
            left: 'center',
            textStyle: {
              fontSize: 16,
              color: '#333'
            }
          },
          tooltip: {
            trigger: 'axis',
            formatter: function(params) {
              let result = params[0].name + '<br/>'
              params.forEach(param => {
                result += param.marker + param.seriesName + ': ' + param.value + ' 个设备<br/>'
              })
              return result
            }
          },
          legend: {
            data: gameNames,
            top: '10%'
          },
          xAxis: {
            type: 'category',
            data: timeLabels,
            axisLabel: {
              rotate: 45,
              fontSize: 12
            }
          },
          yAxis: {
            type: 'value',
            name: '设备数量',
            nameTextStyle: {
              fontSize: 12
            }
          },
          series: series,
          grid: {
            left: '10%',
            right: '10%',
            bottom: '15%',
            top: '25%'
          }
        }

        comparisonChartInstance.setOption(comparisonOption)
      }
    }

    // 加载统计数据
    const loadStatistics = async () => {
      if (!selectedGame.value) {
        return
      }
      
      loading.value = true
      try {
        const gameParam = selectedGame.value.trim()
        const response = await getUserActivityStatistics(gameParam)
        
        // 注意：axios拦截器已经返回了response.data，所以response就是响应体
        if (response.success) {
          const data = response.data
          
          // 单个游戏的统计数据
          if (data.statistics) {
            singleGameStats.value = data.statistics
          } else if (data.allGames && data.allGames[selectedGame.value]) {
            singleGameStats.value = data.allGames[selectedGame.value]
          } else {
            singleGameStats.value = []
          }
          
          // 初始化图表
          initCharts()
        } else {
          ElMessage.error(response.message || '获取统计数据失败')
        }
      } catch (error) {
        console.error('获取统计数据失败:', error)
        ElMessage.error('获取统计数据失败: ' + (error.response?.data?.message || error.message))
      } finally {
        loading.value = false
      }
    }

    // 游戏选择改变
    const onGameChange = () => {
      loadStatistics()
    }

    // 刷新数据
    const refreshData = () => {
      loadStatistics()
    }

    // 格式化时间
    const formatTime = (date) => {
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      })
    }

    // 加载活跃用户列表
    const loadActiveUsers = async () => {
      if (!currentIntervalMinutes.value) {
        return
      }
      
      userListLoading.value = true
      try {
        const response = await getActiveUsersList(
          currentGame.value, 
          currentIntervalMinutes.value, 
          userPage.value, 
          userPageSize.value
        )
        
        // 注意：axios拦截器已经返回了response.data，所以response就是响应体
        if (response.success) {
          const data = response.data
          activeUsersList.value = data.users || []
          userTotal.value = data.total || 0
        } else {
          ElMessage.error(response.message || '获取用户列表失败')
        }
      } catch (error) {
        console.error('获取活跃用户列表失败:', error)
        ElMessage.error('获取活跃用户列表失败: ' + (error.response?.data?.message || error.message))
      } finally {
        userListLoading.value = false
      }
    }

    // 显示活跃用户列表
    const showActiveUsers = async (row, gameName = null) => {
      const game = gameName || selectedGame.value || '冒险岛枫星'
      const intervalMinutes = timeRangeMap[row.timeRange]
      
      if (!intervalMinutes) {
        ElMessage.error('无法获取时间段信息')
        return
      }
      
      userDialogVisible.value = true
      currentGame.value = game
      currentTimeRange.value = row.timeRangeLabel
      currentIntervalMinutes.value = intervalMinutes
      userPage.value = 1
      userPageSize.value = 100
      activeUsersList.value = []
      userTotal.value = 0
      
      await loadActiveUsers()
    }

    // 处理分页大小变化
    const handleUserPageSizeChange = (val) => {
      userPageSize.value = val
      userPage.value = 1
      loadActiveUsers()
    }

    // 处理页码变化
    const handleUserPageChange = (val) => {
      userPage.value = val
      loadActiveUsers()
    }

    // 页面加载时初始化
    onMounted(async () => {
      await loadGames()
      // loadGames 中已经设置了默认游戏，这里直接加载统计数据
      if (selectedGame.value) {
        await loadStatistics()
      }
    })

    return {
      loading,
      selectedGame,
      games,
      singleGameStats,
      allGamesStats,
      devicesChart,
      usersChart,
      comparisonChart,
      versionChart,
      onGameChange,
      refreshData,
      formatTime,
      Refresh,
      // 用户列表弹窗相关
      userDialogVisible,
      userListLoading,
      activeUsersList,
      currentGame,
      currentTimeRange,
      userTotal,
      userPage,
      userPageSize,
      showActiveUsers,
      handleUserPageSizeChange,
      handleUserPageChange,
      // 版本统计相关
      versionStatistics,
      versionLoading
    }
  }
}
</script>

<style scoped>
.statistics-container {
  padding: 12px 12px 32px;
  background-color: #f5f5f5;
  box-sizing: border-box;
}

/* 顶部工具栏 */
.top-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  padding: 8px 0;
}

.toolbar-left {
  display: flex;
  align-items: center;
}

.page-title-compact {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
}

.toolbar-right {
  display: flex;
  align-items: center;
}

.statistics-content {
  min-height: 400px;
}

.main-content-row {
  margin-bottom: 0;
}

/* 紧凑的卡片样式 */
.chart-card-compact,
.table-card-compact {
  border-radius: 6px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
  height: 100%;
}

.chart-card-compact :deep(.el-card__header) {
  padding: 10px 12px;
  border-bottom: 1px solid #ebeef5;
}

.chart-card-compact :deep(.el-card__body) {
  padding: 10px;
}

.table-card-compact :deep(.el-card__header) {
  padding: 10px 12px;
  border-bottom: 1px solid #ebeef5;
}

.table-card-compact :deep(.el-card__body) {
  padding: 8px;
}

.card-header-compact {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.chart-title {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
}

/* 紧凑的图表容器 */
.chart-container-compact {
  width: 100%;
  height: 320px;
}

.chart-container-compact-version {
  width: 100%;
  height: 350px;
}

.number-highlight {
  font-weight: 600;
  color: #409eff;
  cursor: pointer;
}

.number-highlight:hover {
  color: #66b1ff;
  text-decoration: underline;
}

.no-data {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
}

/* 用户列表弹窗样式 */
.user-dialog-header {
  padding: 16px;
  background-color: #f5f7fa;
  border-radius: 4px;
  margin-bottom: 20px;
}

.user-dialog-header p {
  margin: 8px 0;
  color: #606266;
  font-size: 14px;
}

.user-dialog-header p strong {
  color: #303133;
  margin-right: 8px;
}

/* 响应式设计 */
@media (max-width: 1200px) {
  .main-content-row .el-col {
    margin-bottom: 12px;
  }
  
  .chart-container-compact {
    height: 280px;
  }
  
  .chart-container-compact-version {
    height: 300px;
  }
}

@media (max-width: 768px) {
  .statistics-container {
    padding: 8px;
  }
  
  .top-toolbar {
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
  }
  
  .toolbar-right {
    width: 100%;
    justify-content: space-between;
  }
  
  .chart-container-compact {
    height: 250px;
  }
  
  .chart-container-compact-version {
    height: 280px;
  }
}
</style>
